import { InvitationActions } from './actions';
import { GLOBAL_RESOURCES } from 'src/constants';
import { useInstanceEvaluations } from 'src/hooks';
import { GetInvitationResponse } from 'src/types/rest/managementPortalApi';

// Only needed, if the query params are manually stripped
const FALLBACK_RESOURCE = { id: '', type: GLOBAL_RESOURCES.APPLICATIONS_APP };

/**
 * Shared by every consumer of an invitation's data (the page and its
 * breadcrumb) so they all gate on the exact same authorization check.
 *
 * Exactly one of `applicationId`/`tenantId` is mandatory. The id of the
 * application or tenant the invitation targets comes from the route's
 * `?applicationId=`/`?tenantId=` query params (see `Invitation.tsx`), since
 * evaluating authorization has to happen before fetching the invitation
 * itself.
 */
export const useInvitationAuthorization = ({
  applicationId,
  tenantId,
}: Pick<GetInvitationResponse, 'applicationId' | 'tenantId'>) => {
  const resource = applicationId
    ? { id: applicationId, type: GLOBAL_RESOURCES.APPLICATIONS_APP }
    : tenantId
      ? { id: tenantId, type: GLOBAL_RESOURCES.TM_TENANT }
      : undefined;

  const result = useInstanceEvaluations({
    actions: [...Object.values(InvitationActions)],
    requiredActions: [InvitationActions.READ],
    resource: resource ?? FALLBACK_RESOURCE,
  });

  /**
   * One of `applicationId`/`tenantId` is mandatory. Hooks are still called
   * unconditionally above (per rules-of-hooks) and this throw is reached
   * afterwards, mirroring how `useInstanceEvaluations` itself throws its 403
   * after all of its hooks. A link missing it is malformed, treated as not
   * found, same as `ProtectedRoute` does for unauthorized access.
   */
  if (!resource) {
    throw new Response('Not Found', { status: 404 });
  }

  return result;
};
